// Cast API Service
// Wraps the existing Cast API endpoints with TypeScript interfaces

import { Agent, JobStatus, AgentCreationData } from '../types';

class CastApiService {
  private baseUrl = '/cast/api/cast';

  // List all agents
  async listAgents(): Promise<Agent[]> {
    try {
      const response = await fetch(`${this.baseUrl}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch agents: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  }

  // Get agent information
  async getAgent(agentId: string): Promise<Agent> {
    try {
      const response = await fetch(`${this.baseUrl}/${agentId}/info`);
      if (!response.ok) {
        throw new Error(`Failed to fetch agent: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching agent:', error);
      throw error;
    }
  }

  // Get agent headshot
  async getAgentHeadshot(agentId: string): Promise<string | null> {
    try {
      const response = await fetch(`${this.baseUrl}/${agentId}/info`);
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      return data.headshot_url || null;
    } catch (error) {
      console.error('Error fetching agent headshot:', error);
      return null;
    }
  }

  // Get agent character sheets
  async getAgentCharsheets(agentId: string): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/${agentId}/info`);
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return data.char_sheets || [];
    } catch (error) {
      console.error('Error fetching agent charsheets:', error);
      return [];
    }
  }

  // Get agent auditions
  async getAgentAuditions(agentId: string): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/${agentId}/info`);
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      return data.auditions || [];
    } catch (error) {
      console.error('Error fetching agent auditions:', error);
      return [];
    }
  }

  // Get agent control voices
  async getAgentControlVoices(agentId: string): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/${agentId}/control_voice`);
      if (!response.ok) {
        return [];
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching agent control voices:', error);
      return [];
    }
  }

  // Create a new agent
  async createAgent(data: AgentCreationData): Promise<JobStatus> {
    try {
      const formData = new FormData();
      formData.append('agent_name', data.agent_name);
      formData.append('bio', data.bio);
      formData.append('headshot_bg', data.headshot_bg);
      formData.append('control_wav', data.control_wav);

      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Provide more specific error messages for common issues
        if (response.status === 413) {
          throw new Error(
            `File too large. One or more files exceed the 20MB limit. ` +
            `Headshot: ${(data.headshot_bg.size / (1024 * 1024)).toFixed(1)}MB, ` +
            `Voice: ${(data.control_wav.size / (1024 * 1024)).toFixed(1)}MB`
          );
        }

        throw new Error(errorData.detail || `Failed to create agent: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating agent:', error);
      throw error;
    }
  }

  // Get job status
  async getJobStatus(jobId: string): Promise<JobStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}`);
      if (!response.ok) {
        throw new Error(`Failed to get job status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching job status:', error);
      throw error;
    }
  }

  // List all jobs
  async listJobs(): Promise<JobStatus[]> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs`);
      if (!response.ok) {
        throw new Error(`Failed to list jobs: ${response.status}`);
      }
      const data = await response.json();
      return data.jobs || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }

  // Validate agent exists
  async validateAgent(agentId: string): Promise<boolean> {
    try {
      const agent = await this.getAgent(agentId);
      return !!agent && !!agent.id;
    } catch (error) {
      return false;
    }
  }
}

// Export singleton instance
export const castApi = new CastApiService();